import { useState } from 'react';
import {
  ARREARS_TERMS,
  INSTRUMENT_CLASSES,
  INSTRUMENT_CLASS_LABELS,
  INSTRUMENT_TIERS,
  INSTRUMENT_TIER_LABELS,
  PROVENANCES,
  PROVENANCE_LABELS,
  isPriceable,
  requiredMarketInputsFor,
  requiredTermsFor,
  tierRequiresReviewer,
  type Entity,
  type Instrument,
  type InstrumentClass,
  type InstrumentMarketInput,
  type InstrumentTier,
  type Provenance,
} from '@ave/shared';
import { api } from '../lib/api';
import { TermField, termLabel } from './instrumentTerms';
import { money } from '../lib/format';
import { Badge, Card, EmptyState, ProvenanceChip, SectionHeading } from './ui';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const MARKET_INPUT_LABELS: Record<InstrumentMarketInput, string> = {
  volatility: 'Volatility (annualised, as a decimal)',
  risk_free_rate: 'Risk-free rate (as a decimal)',
  dividend_yield: 'Dividend yield (as a decimal)',
  discount_rate: 'Discount rate (as a decimal)',
  credit_spread: 'Credit spread (as a decimal)',
  underlying_price_per_share: 'Underlying value per share',
  arrears_recoverability: 'Proportion of the arrears realistically payable',
};

const TIER_SHORT: Record<InstrumentTier, string> = {
  t1_quoted: 'Level 1',
  t2_observable: 'Level 2',
  t3_judgement: 'Level 3',
};

/**
 * The instruments library: what the company has issued, what each is worth,
 * and whether that figure may be used yet.
 *
 * The card is built around a separation the rest of the system already
 * enforces and nothing on screen previously showed. **Terms are read off the
 * paperwork; market inputs are chosen by a valuer.** An option agreement
 * states the exercise price and the expiry date. It does not state the
 * volatility — that comes from a comparable set somebody selected and has to
 * defend, and the classification model is forbidden to supply one. So the
 * terms are captured once when the instrument is recorded, and the volatility
 * and rates are asked for again every time it is priced, alongside the
 * evidence they came from.
 *
 * That is why pricing is a form rather than a button. A button would imply
 * the inputs were already known, and the one input that most moves the answer
 * never is.
 *
 * **A computed price is not a usable price.** A Level 3 instrument rests on
 * inputs nobody can look up, so IFRS 13 puts a reviewer between the
 * calculation and its use (§9.2). The row shows the figure and says plainly
 * that it is not usable yet, rather than hiding it until approval — the
 * reviewer has to see the number in order to approve it. Repricing withdraws
 * the approval, and the button says so before it is pressed.
 */
export function Instruments({
  engagementId,
  instruments,
  entities,
  canWrite,
  canReview,
  onChange,
}: {
  engagementId: string;
  instruments: Instrument[];
  entities: Entity[];
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
}) {
  return (
    <Card>
      <SectionHeading
        title="Instruments"
        subtitle="What has been issued, what it is worth, and whether that figure may be used"
      />

      {instruments.length === 0 ? (
        <EmptyState
          title="No instruments recorded"
          detail="Options, warrants, loan notes, convertibles and preference shares are priced by the engine. Anything else can still be recorded and classified — the library is the record of what exists, not only of what has a model."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {instruments.map((instrument) => (
            <InstrumentRow
              key={instrument.id}
              engagementId={engagementId}
              instrument={instrument}
              canWrite={canWrite}
              canReview={canReview}
              onChange={onChange}
            />
          ))}
        </ul>
      )}

      {canWrite ? (
        <RecordInstrument engagementId={engagementId} entities={entities} onSaved={onChange} />
      ) : null}
    </Card>
  );
}

function InstrumentRow({
  engagementId,
  instrument,
  canWrite,
  canReview,
  onChange,
}: {
  engagementId: string;
  instrument: Instrument;
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [pricing, setPricing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = instrument.latestRun;
  const priced = run !== null && run !== undefined && run.status === 'ok';
  const value = priced ? (run.outputs.total_value as string | undefined) : undefined;
  const needsReviewer = tierRequiresReviewer(instrument.tier);
  const priceable = isPriceable(instrument.cls);

  const missingTerms = isPriceable(instrument.cls)
    ? requiredTermsFor(instrument.cls, instrument.terms).filter((key) => {
        const v = instrument.terms[key];
        return v === undefined || v === null || v === '';
      })
    : [];

  async function approve() {
    setBusy(true);
    setError(null);
    try {
      await api.approveInstrumentPrice(engagementId, instrument.id);
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve the price');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li data-item-id={instrument.id} className="scroll-mt-24 px-6 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-slate-800">{instrument.name}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Badge tone="slate">{INSTRUMENT_CLASS_LABELS[instrument.cls]}</Badge>
            {/* Short on the badge, the whole IFRS 13 description on hover:
                "Level 3" is what a reader scans for, and "significant inputs
                unobservable" is what they want when they stop to ask. */}
            <Badge tone={needsReviewer ? 'amber' : 'slate'} title={INSTRUMENT_TIER_LABELS[instrument.tier]}>
              {TIER_SHORT[instrument.tier]}
            </Badge>
            <ProvenanceChip provenance={instrument.provenance} />
            {instrument.holder ? <Badge tone="slate">held by {instrument.holder}</Badge> : null}
          </div>
        </div>

        <div className="text-right">
          {priced ? (
            <>
              <p className="text-base font-medium tabular-nums text-slate-900">{money(value)}</p>
              {instrument.priceUsable ? (
                <Badge tone="emerald">approved for use</Badge>
              ) : needsReviewer ? (
                <Badge tone="amber">computed — not usable until approved</Badge>
              ) : (
                <Badge tone="slate">priced</Badge>
              )}
            </>
          ) : (
            <Badge tone="slate">not priced</Badge>
          )}
        </div>
      </div>

      {/* What the paperwork said. Shown because a price is only as good as the
          terms behind it, and a reader checking one wants the other. */}
      {Object.keys(instrument.terms).length > 0 ? (
        <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {Object.entries(instrument.terms).map(([key, val]) => (
            <div key={key} className="flex justify-between gap-3">
              <dt className="text-slate-500">{termLabel(key)}</dt>
              <dd className="tabular-nums text-slate-700">{String(val)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {!priceable ? (
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          The engine has no model for a {INSTRUMENT_CLASS_LABELS[instrument.cls].toLowerCase()}. It
          is recorded and classified here; its value comes from elsewhere.
        </p>
      ) : missingTerms.length > 0 ? (
        <p className="mt-3 text-sm leading-relaxed text-amber-700">
          Cannot be priced until the paperwork has been read for{' '}
          {missingTerms.map((k) => termLabel(k)).join(', ')}.
        </p>
      ) : null}

      {priced && instrument.latestRun ? (
        <p className="mt-2 text-xs text-slate-400">
          Priced by {String(instrument.latestRun.libraryVersions?.quantlib ?? 'the engine')
            ? `QuantLib ${instrument.latestRun.libraryVersions?.quantlib ?? ''}`.trim()
            : 'the engine'}
          , engine {instrument.latestRun.engineVersion}
        </p>
      ) : null}

      {error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {canWrite && priceable && missingTerms.length === 0 ? (
          <button type="button" className="btn-secondary" onClick={() => setPricing(true)}>
            {priced ? 'Reprice' : 'Price'}
          </button>
        ) : null}
        {canReview && needsReviewer && priced && !instrument.priceUsable ? (
          <button type="button" className="btn-primary" onClick={approve} disabled={busy}>
            Approve this price
          </button>
        ) : null}
      </div>

      {pricing ? (
        <PriceForm
          engagementId={engagementId}
          instrument={instrument}
          alreadyApproved={Boolean(instrument.priceUsable && needsReviewer)}
          onClose={() => setPricing(false)}
          onSaved={async () => {
            setPricing(false);
            await onChange();
          }}
        />
      ) : null}
    </li>
  );
}

/**
 * The market inputs, asked for at the moment they are used.
 *
 * Every field carries a place for the evidence behind it. Volatility is the
 * input a reviewer challenges first and the one that moves the answer most,
 * so the form makes recording where it came from the ordinary thing to do
 * rather than an afterthought.
 */
function PriceForm({
  engagementId,
  instrument,
  alreadyApproved,
  onClose,
  onSaved,
}: {
  engagementId: string;
  instrument: Instrument;
  alreadyApproved: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const required = isPriceable(instrument.cls)
    ? requiredMarketInputsFor(instrument.cls, instrument.terms)
    : [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = required.every((key) => (values[key] ?? '').trim() !== '');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.priceInstrument(
        engagementId,
        instrument.id,
        required.map((key) => ({
          key,
          value: values[key]!.trim(),
          evidenceRefs: (evidence[key] ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        })),
      );
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not price the instrument');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-sm font-medium text-slate-800">Market inputs</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        These are not terms of the instrument and are not stored on it. They are chosen for this
        run, recorded with it, and asked for again next time — because the answer moves with them
        and somebody has to stand behind the choice.
      </p>

      {alreadyApproved ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-800">
          This price has been approved. Repricing withdraws that approval, because an approval that
          survives its own inputs changing is a reviewer&apos;s name against a figure they never
          saw. It will need approving again.
        </p>
      ) : null}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {required.map((key) => (
          <div key={key}>
            <label className="text-sm text-slate-600">
              {MARKET_INPUT_LABELS[key]}
              <input
                className={INPUT}
                value={values[key] ?? ''}
                inputMode="decimal"
                placeholder={key === 'volatility' ? '0.45' : ''}
                onChange={(ev) => setValues((v) => ({ ...v, [key]: ev.target.value }))}
              />
            </label>
            <label className="mt-1.5 block text-xs text-slate-500">
              Evidence for it
              <input
                className={INPUT}
                value={evidence[key] ?? ''}
                placeholder="evidence_point:comparable-volatility"
                onChange={(ev) => setEvidence((v) => ({ ...v, [key]: ev.target.value }))}
              />
            </label>
          </div>
        ))}
      </div>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Pricing…' : 'Price it'}
        </button>
      </div>
    </div>
  );
}

/**
 * Recording an instrument.
 *
 * The terms asked for follow the class, and only the ones the engine needs to
 * price it are demanded — a class with no model can still be recorded with
 * nothing but a name, because the library is the record of what the company
 * has issued and not only of what can be valued.
 */
function RecordInstrument({
  engagementId,
  entities,
  onSaved,
}: {
  engagementId: string;
  entities: Entity[];
  onSaved: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entityId, setEntityId] = useState<string>(entities[0]?.id ?? '');
  const [name, setName] = useState('');
  const [holder, setHolder] = useState('');
  const [cls, setCls] = useState<InstrumentClass>('option');
  const [tier, setTier] = useState<InstrumentTier>('t3_judgement');
  const [provenance, setProvenance] = useState<Provenance>('data_derived');
  const [rationale, setRationale] = useState('');
  const [terms, setTerms] = useState<Record<string, string>>({});
  const [redeemable, setRedeemable] = useState(true);
  const [inArrears, setInArrears] = useState(false);

  // The arrears amount is what turns the rest of the block on, so an unticked
  // box has to mean the amount is absent rather than blank: a blank amount
  // with a ranking beside it would ask the engine to value nothing.
  const wanted = isPriceable(cls)
    ? requiredTermsFor(cls, {
        ...(cls === 'pref_share' ? { redeemable } : {}),
        ...(inArrears ? { arrears_amount: terms.arrears_amount ?? '0' } : {}),
      })
    : [];
  const valid = name.trim() !== '' && entityId !== '' && wanted.every((k) => (terms[k] ?? '').trim() !== '');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const key of wanted) payload[key] = terms[key]!.trim();
      if (cls === 'pref_share') payload.redeemable = redeemable;

      await api.createInstrument(engagementId, {
        entityId,
        name: name.trim(),
        holder: holder.trim() || undefined,
        cls,
        tier,
        provenance,
        classificationRationale: rationale.trim() || undefined,
        terms: payload,
      });
      setOpen(false);
      setName('');
      setHolder('');
      setTerms({});
      setRationale('');
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the instrument');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="border-t border-slate-100 px-6 py-4">
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          Record an instrument
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Entity that issued it
          <select className={SELECT} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          What it is called
          <input
            className={INPUT}
            value={name}
            placeholder="2024 unapproved option — A. Employee"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Class
          <select
            className={SELECT}
            value={cls}
            onChange={(e) => {
              setCls(e.target.value as InstrumentClass);
              setTerms({});
            }}
          >
            {INSTRUMENT_CLASSES.map((c) => (
              <option key={c} value={c}>
                {INSTRUMENT_CLASS_LABELS[c]}
                {isPriceable(c) ? '' : ' — no model'}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Fair value hierarchy
          <select
            className={SELECT}
            value={tier}
            onChange={(e) => setTier(e.target.value as InstrumentTier)}
          >
            {INSTRUMENT_TIERS.map((t) => (
              <option key={t} value={t}>
                {INSTRUMENT_TIER_LABELS[t]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Held by
          <input
            className={INPUT}
            value={holder}
            placeholder="Optional"
            onChange={(e) => setHolder(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          How this was established
          <select
            className={SELECT}
            value={provenance}
            onChange={(e) => setProvenance(e.target.value as Provenance)}
          >
            {PROVENANCES.map((p) => (
              <option key={p} value={p}>
                {PROVENANCE_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {tierRequiresReviewer(tier) ? (
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          A Level 3 price rests on inputs nobody can look up, so it will not be usable until a
          reviewer has approved it.
        </p>
      ) : null}

      {cls === 'pref_share' ? (
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={redeemable}
            onChange={(e) => {
              setRedeemable(e.target.checked);
              setTerms({});
            }}
          />
          Redeemable — it has a redemption date. Irredeemable shares are a perpetuity and need no
          dates.
        </label>
      ) : null}

      {cls === 'pref_share' ? (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={inArrears}
            onChange={(e) => {
              setInArrears(e.target.checked);
              setTerms((t) => {
                const next = { ...t };
                for (const key of ARREARS_TERMS) delete next[key];
                return next;
              });
            }}
          />
          Dividends have been passed and are in arrears
        </label>
      ) : null}

      {inArrears ? (
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          Only periods that have already fallen due and were not paid. The dividend accruing in the
          current period is not an arrear — it is already in the price, and counting it here would
          add a period's dividend twice.
        </p>
      ) : null}

      {wanted.length > 0 ? (
        <>
          <p className="mt-4 text-sm font-medium text-slate-800">Terms, from the paperwork</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            Only what the instrument itself says. The volatility and the rates are chosen when it is
            priced, not read off the document.
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {wanted.map((key) => (
              <TermField
                key={key}
                termKey={key}
                value={terms[key] ?? ''}
                onChange={(value) => setTerms((t) => ({ ...t, [key]: value }))}
              />
            ))}
          </div>
        </>
      ) : null}

      <label className="mt-3 block text-sm text-slate-600">
        Why it is classified this way
        <textarea
          className={INPUT}
          rows={2}
          value={rationale}
          placeholder="Optional, and the first thing asked about a Level 3"
          onChange={(e) => setRationale(e.target.value)}
        />
      </label>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          Record it
        </button>
      </div>
    </div>
  );
}
